// Copyright (c) StrangeBee 2023

import { AuthController, MFACodeOutput, PasswordPolicyOutput } from 'thehive-sdk';

export interface AuthEventList {
    onGetPasswordPolicy: () => Promise<PasswordPolicyOutput>;
    sendPasswordReset: (login: string) => void;
    changePassword: ({ password, secret }: { password: string; secret: string }) => Promise<void>;
    onGetMFA: () => Promise<MFACodeOutput | void>;
    onSetMFA: (code: number, secret: string) => Promise<void>;
    onUnsetMFA: (userId: string) => Promise<void>;
}

interface AuthEventListParams {
    controller: AuthController;
    onSuccess: (count?: boolean, message?: string) => void;
    onError: (error: any) => void;
    locale: (...args: any[]) => any;
}

export const computeEventList = ({ controller, onSuccess, onError, locale }: AuthEventListParams): AuthEventList => ({
    onGetPasswordPolicy: (): Promise<PasswordPolicyOutput> => controller.getPasswordPolicy(),
    sendPasswordReset: (login): void => {
        controller
            .sendPasswordMail(login)
            .then(() => onSuccess(false, locale('notification.user.reset-password-sent')))
            .catch(onError);
    },
    changePassword: ({ password, secret }) =>
        controller
            .changePassword({ password, secret })
            .then(() => onSuccess(false, locale('notification.user.change-password')))
            .catch((e) => {
                const error = e;

                if (error.message === 'Token has expired') {
                    error.errorCode = locale('login.reset.expired.type');
                    error.message = locale('login.reset.expired.message');
                }

                onError(error);

                throw error;
            }),
    onGetMFA: () => controller.getMFASecret().catch(onError),
    onSetMFA: (code, secret) =>
        controller
            .setMFASecret(code, secret)
            .then(() => onSuccess(false, locale('notification.mfa.set')))
            .catch(onError),
    onUnsetMFA: (userId) =>
        controller
            .unsetMFAForUser(userId)
            .then(() => onSuccess?.(false, locale('notification.mfa.unset')))
            .catch(onError),
});
